import React from 'react';
import classes from './SocialProfileListItem.module.scss';
import { profilesHelpers } from '../../../modules/Settings/Profiles/Profiles.helpers';
import {
  CoreBitlyFieldsFragment,
  InstagramApiType,
  PostTikTokPrivacy,
  SocialOAuthProvider,
  SocialProfileFields_InstagramSocialProfile_Fragment,
  SocialProfileFieldsFragment,
  TikTokSocialProfile
} from '../../../models/graphql';
import { SocialProfile } from '../SocialProfile';
import { DirectPublishingStatus } from '../DirectPublishingStatus';
import { ReconnectButton } from '../ReconnectButton';
import classNames from 'classnames';
import { ContainerListItem } from '../../actions/ListItem/ListItem.view';
import { ProfilesViewProps } from '../../../modules/Settings/Profiles/Profiles.view';
import { ButtonMenu } from '../../actions/ButtonMenu';

export type MappedProfileType = SocialProfileFieldsFragment | CoreBitlyFieldsFragment;

export type TikTokProfileSettingsType = Pick<
  TikTokSocialProfile,
  'privacy' | 'commentDisabled' | 'duetDisabled' | 'stitchDisabled' | 'maxVideoDuration' | 'id'
>;

export type TikTokProfileValues = {
  allowedActions: Array<'comment' | 'duet' | 'stitch'>;
  privacyTarget: PostTikTokPrivacy | null;
  promotion: Array<'self' | 'other'>;
};

export type SocialProfileListItemViewProps = {
  profile: MappedProfileType;
  setDirectPublishing?: ProfilesViewProps['setDirectPublishing'];
  listItems: ContainerListItem[];
  reconnect: VoidFunction | undefined;
};

export const SocialProfileListItemView = (props: SocialProfileListItemViewProps): JSX.Element => {
  const { profile, listItems, setDirectPublishing, reconnect } = props;

  const genericProfile = profile.__typename === 'GenericSocialProfile' ? profile.genericProfile : undefined;
  const oAuthProviderTypename = profilesHelpers.getOAuthProviderTypename(profile.__typename);
  const networkTypename = profilesHelpers.mapNetworkFromTypename(profile.__typename);

  const profileWrapperClasses = classNames(classes.ProfilesPanel);
  const labelMaxWidthClasses = classNames('cell', 'auto', classes.LabelWidth);

  const isInvalidDirectPublishingType =
    profile.__typename === 'InstagramSocialProfile' &&
    profile.apiType !== InstagramApiType.FACEBOOK_GRAPH &&
    profile.apiType !== InstagramApiType.INSTAGRAM_GRAPH;

  if (oAuthProviderTypename || genericProfile) {
    return (
      <div className={profileWrapperClasses}>
        <div className={'grid-x align-justify'}>
          <div className={labelMaxWidthClasses}>
            <SocialProfile
              size={'large'}
              label={true}
              sublabel={true}
              socialProfile={profilesHelpers.remapSocialProfile(profile, true, false, false)}
              {...(profile.__typename !== 'Bitly' ? { image: (profile as SocialProfileFieldsFragment).photoUrl } : {})}
              disconnected={!profile.connected}
            />
          </div>
          <div className={'cell shrink'}>
            <div className={'grid-x align-left align-middle'}>
              {oAuthProviderTypename === SocialOAuthProvider.INSTAGRAM &&
              profile.connected &&
              setDirectPublishing &&
              !isInvalidDirectPublishingType ? (
                <DirectPublishingStatus
                  breakpoint={'large'}
                  profile={profile as SocialProfileFields_InstagramSocialProfile_Fragment}
                  setDirectPublishing={setDirectPublishing}
                />
              ) : null}
              {oAuthProviderTypename && reconnect && (
                <ReconnectButton
                  action={reconnect}
                  breakpoint={'small'}
                  connected={profile.connected}
                  oAuthProvider={oAuthProviderTypename}
                  network={networkTypename}
                />
              )}
              <div className={'cell shrink'}>
                <div className={classes.SquareButtonWrapper}>
                  <ButtonMenu buttonType={'transparent'} listItems={listItems} side={'right'} isSquareButton={true} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {oAuthProviderTypename && reconnect && (
          <ReconnectButton
            action={reconnect}
            breakpoint={'large'}
            connected={profile.connected}
            oAuthProvider={oAuthProviderTypename}
            network={networkTypename}
          />
        )}
        {oAuthProviderTypename === SocialOAuthProvider.INSTAGRAM && profile.connected && setDirectPublishing ? (
          <DirectPublishingStatus
            breakpoint={'small'}
            profile={profile as SocialProfileFields_InstagramSocialProfile_Fragment}
            setDirectPublishing={setDirectPublishing}
          />
        ) : null}
      </div>
    );
  } else {
    throw new Error('No network passed for getProfilesByStatus function');
  }
};
